import { Link, Navigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMe } from "@/context/MeContext";
import { Stat, tabCls } from "@/components/dashboard/Shared";
import { PressKitEditor, SubmissionsTable, TrackList, UploadTrack, useArtistData } from "@/components/dashboard/ArtistParts";
import { Inbox, MyMedia, RadioQueue } from "@/components/dashboard/CuratorParts";
import { CuratorShows } from "@/components/dashboard/CuratorShows";
import { CuratorPayouts, PaySettings, PayoutsAdmin } from "@/components/dashboard/MoneyParts";
import { ProStats, ProStatus } from "@/components/dashboard/ProStats";
import { ShowsAdmin } from "@/components/dashboard/ShowsAdmin";
import { AdminStats, AdminUpload, ChannelsAdmin, MediaAdmin, TracksAdmin, UsersAdmin } from "@/components/dashboard/AdminParts";

const Title = ({ kicker, title }) => (
  <div className="mb-10">
    <p className="font-mono text-xs tracking-widest text-amber-500/90">{kicker}</p>
    <h1 className="mt-3 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">{title}</h1>
  </div>
);

const Panel = ({ tabs, testId }) => (
  <Tabs defaultValue={tabs[0][0]} data-testid={testId}>
    <TabsList className="mb-8 h-auto flex-wrap gap-1 rounded-full border border-white/10 bg-[#12121A] p-1">
      {tabs.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className={tabCls}>{l}</TabsTrigger>)}
    </TabsList>
    {tabs.map(([v, , node]) => <TabsContent key={v} value={v}>{node}</TabsContent>)}
  </Tabs>
);

function ArtistDashboard({ me }) {
  const { tracks, subs, reload } = useArtistData();
  const plays = tracks.reduce((a, t) => a + (t.plays || 0), 0);
  const feedback = subs.filter((s) => s.feedback);
  const summary = (
    <div className="space-y-8">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat id="credits" label="Créditos" value={me.credits} accent />
        <Stat id="plays" label="Reproducciones en radio" value={plays} />
        <Stat id="pending" label="Envíos en espera" value={subs.filter((s) => s.status === "pending").length} />
        <Stat id="responses" label="Respuestas recibidas" value={subs.filter((s) => ["aired", "not_aired"].includes(s.status)).length} />
      </div>
      <ProStatus me={me} />
      <div className="flex flex-wrap gap-3">
        <Link to="/creditos" data-testid="artist-buy-credits" className="rounded-full bg-[#FF4500] px-5 py-2.5 text-sm font-semibold text-white">Comprar créditos</Link>
        <Link to="/medios" data-testid="artist-go-directory" className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10">Enviar a medios</Link>
      </div>
      <div>
        <h2 className="font-heading text-xl font-bold text-white">Opiniones recibidas</h2>
        <div className="mt-4"><SubmissionsTable subs={feedback} /></div>
      </div>
    </div>
  );
  return (
    <Panel testId="artist-dashboard" tabs={[
      ["resumen", "Resumen", summary],
      ["canciones", "Mis canciones", <div className="space-y-8"><UploadTrack onDone={reload} /><TrackList tracks={tracks} reload={reload} /></div>],
      ["estadisticas", "Estadísticas", <ProStats me={me} />],
      ["presskit", "Press Kit", <PressKitEditor />],
      ["envios", "Envíos a medios", <SubmissionsTable subs={subs} />],
    ]} />
  );
}

function CuratorDashboard({ me, refresh }) {
  return (
    <Panel testId="curator-dashboard" tabs={[
      ["radio", "Aprobar para la radio", <RadioQueue />],
      ["inbox", "Envíos recibidos", <Inbox onLoad={refresh} />],
      ["programas", "Mis programas", <CuratorShows />],
      ["medio", "Mi medio", <MyMedia />],
      ["saldo", "Saldo y retiros", <CuratorPayouts />],
    ]} />
  );
}

function AdminDashboard() {
  return (
    <Panel testId="admin-dashboard" tabs={[
      ["stats", "Ingresos", <AdminStats />],
      ["canales", "Canales", <ChannelsAdmin />],
      ["usuarios", "Usuarios", <UsersAdmin />],
      ["medios", "Medios", <MediaAdmin />],
      ["subir", "Subir música", <AdminUpload />],
      ["programas", "Programas en vivo", <ShowsAdmin />],
      ["pistas", "Canciones", <TracksAdmin />],
      ["radio", "Aprobar radio", <RadioQueue />],
      ["inbox", "Envíos a medios", <Inbox />],
      ["retiros", "Retiros", <PayoutsAdmin />],
      ["ajustes", "Ajustes de pago", <PaySettings />],
    ]} />
  );
}

export default function Dashboard() {
  const { me, loading, refresh } = useMe();
  if (loading) return <div className="h-[60vh]" />;
  if (!me) return <Navigate to="/sign-in" replace />;
  if (!me.onboarded) return <Navigate to="/bienvenida" replace />;
  const titles = { artist: ["PANEL DEL ARTISTA", me.display_name], curator: ["PANEL DEL CURADOR", me.display_name], admin: ["ADMINISTRACIÓN", "RARA PERLA"] };
  const [kicker, title] = titles[me.role];
  return (
    <div className="mx-auto max-w-[1400px] px-4 py-16 md:px-8">
      <Title kicker={kicker} title={title} />
      {me.role === "artist" && <ArtistDashboard me={me} />}
      {me.role === "curator" && <CuratorDashboard me={me} refresh={refresh} />}
      {me.role === "admin" && <AdminDashboard />}
    </div>
  );
}
